import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import Group, { makeGroupWithName } from "../../groups/Group";
import Term from "../../term/Term";
import styles from "../css/LearningReviewGroupSelectScreen.module.css";
import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import LearningReviewGroup, {
  sortByIncorrectTermsCountLearningReviewGroup,
  sortByNameLearningReviewGroup,
  sortByTotalTermsCountLearningReviewGroup,
  sortByTotalTermsCountReverseLearningReviewGroup,
} from "../model/LearningReviewGroup";
import {
  extractCorrectAnswersWithGroup,
  extractGroupsFromTerms,
  extractIncorrectAnswersWithGroup,
} from "../../term/util/TermDecomposer";
import { LearningAnswerType } from "../model/LearningAnswerType";
import LearningReviewGroupCell from "./LearningReviewGroupCell";
import SpacingView from "../../common/view/SpaingView";
import DropDownMenu from "../../common/DropDownMenu";
import { getLearningReviewSortStyleTitle } from "../util/learning";
import BasicMenu from "../../common/BasicMenu";
import {
  getLearningReviewGroupSortStyleFromLocalStorage,
  setLearningReviewGroupSortStyleToLocalStorage,
} from "../../settings/util/learningSettings";
import { LearningReviewSortStyle } from "../model/LearningReviewSortStyle";
import ListEmptyText from "../../common/ListEmptyText";
import { useBackToClose } from "../../../hooks/useBackToClose";

interface LearningReviewGroupSelectScreenProps {
  open: boolean;
  terms: Term[];
  selectedAnswerTypes: LearningAnswerType[];
  onSelectAllGroup: () => void;
  onSelectGroups: (groups: Group[]) => void;
  onClose: () => void;
}

export default function LearningReviewGroupSelectScreen(
  props: LearningReviewGroupSelectScreenProps
) {
  useBackToClose(props.open, props.onClose);
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [learningReviewGroups, setLearningReviewGroups] = useState<
    LearningReviewGroup[]
  >([]);
  const [enabledDoneButton, setEnabledDoneButton] = useState(false);

  const [count, setCount] = useState<string>("0");
  const [sortStyleTitle, setSortStyleTitle] = useState("");
  const [sortStyle, setSortStyle] = useState<LearningReviewSortStyle>(
    LearningReviewSortStyle.named
  );

  const [openSortStyleMenu, setOpenSortStyleMenu] = useState(false);

  useEffect(() => {
    const style = getLearningReviewGroupSortStyleFromLocalStorage();
    initLearningReviewGroup(props.terms, style);
    setSortStyle(style);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    const hasSelected = learningReviewGroups.some((item) => item.isSelected);
    setEnabledDoneButton(hasSelected);

    const count = getTotalTermsCount();
    const localized = t("LearningReviewTermCountText", { count });
    setCount(localized);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningReviewGroups]);

  useEffect(() => {
    setLearningReviewGroupSortStyleToLocalStorage(sortStyle);
    setSortStyleTitle(getLearningReviewSortStyleTitle());
    sortList(sortStyle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortStyle]);

  function initLearningReviewGroup(
    terms: Term[],
    style: LearningReviewSortStyle
  ) {
    var newItems: LearningReviewGroup[] = [];

    const groups = extractGroupsFromTerms(terms);
    groups.forEach((group: Group) => {
      let correctTermsCount = extractCorrectAnswersWithGroup(
        terms,
        group.title
      ).length;
      let incorrectTermsCount = extractIncorrectAnswersWithGroup(
        terms,
        group.title
      ).length;
      let totalTermsCount = makeTotalTermsCount(group.title, terms);

      const newLearningReviewGroup: LearningReviewGroup =
        new LearningReviewGroup(
          false,
          group.title.length === 0,
          group.title.length === 0 ? t("ThereIsNoGroup") : group.title,
          totalTermsCount,
          correctTermsCount,
          incorrectTermsCount,
          false
        );

      newItems.push(newLearningReviewGroup);
    });

    if (newItems.length !== 0) {
      const allGroup = makeAllGroup(newItems);
      newItems.push(allGroup);
    }

    setLearningReviewGroups(sortItems(newItems, style));
  }

  function sortItems(
    items: LearningReviewGroup[],
    style: LearningReviewSortStyle
  ): LearningReviewGroup[] {
    switch (style) {
      case LearningReviewSortStyle.named:
        return sortByNameLearningReviewGroup(items);
      case LearningReviewSortStyle.termsCount:
        return sortByTotalTermsCountLearningReviewGroup(items);
      case LearningReviewSortStyle.termsCountReverse:
        return sortByTotalTermsCountReverseLearningReviewGroup(items);
      case LearningReviewSortStyle.incorrectTermsCount:
        return sortByIncorrectTermsCountLearningReviewGroup(items);
    }
  }

  function sortList(style: LearningReviewSortStyle) {
    if (learningReviewGroups.length === 0) return;
    setLearningReviewGroups(sortItems(learningReviewGroups, style));
  }

  function makeTotalTermsCount(groupName: string, terms: Term[]): number {
    let count = 0;

    if (props.selectedAnswerTypes.includes(LearningAnswerType.correct)) {
      count += extractCorrectAnswersWithGroup(terms, groupName).length;
    }

    if (props.selectedAnswerTypes.includes(LearningAnswerType.incorrect)) {
      count += extractIncorrectAnswersWithGroup(terms, groupName).length;
    }

    return count;
  }

  function makeAllGroup(items: LearningReviewGroup[]): LearningReviewGroup {
    const groupName = t("AllGroups");

    const correctTermsCount = items.reduce(
      (sum, item) => sum + item.correctTermsCount,
      0
    );
    const incorrectTermsCount = items.reduce(
      (sum, item) => sum + item.incorrectTermsCount,
      0
    );
    const totalTermsCount = items.reduce(
      (sum, item) => sum + item.totalTermsCount,
      0
    );

    return new LearningReviewGroup(
      true,
      false,
      groupName,
      totalTermsCount,
      correctTermsCount,
      incorrectTermsCount,
      false
    );
  }

  function getTotalTermsCount(): number {
    const selectedItems = learningReviewGroups.filter(
      (item) => item.isSelected
    );
    if (selectedItems.length === 0) return 0;

    const hasSelectedAllGroup = learningReviewGroups.some(
      (item) => item.isAllGroup && item.isSelected
    );
    if (hasSelectedAllGroup) {
      return learningReviewGroups
        .filter((item) => item.isSelected && !item.isAllGroup)
        .reduce((sum, item) => sum + item.totalTermsCount, 0);
    }

    return selectedItems.reduce((sum, item) => sum + item.totalTermsCount, 0);
  }

  function handleOnClickGroupSelectContainer(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    e.stopPropagation();
  }

  function handleOnClickDoneButton() {
    if (!learningReviewGroups.some((item) => item.isSelected)) return;

    if (
      learningReviewGroups.some((item) => item.isSelected && item.isAllGroup)
    ) {
      props.onSelectAllGroup();
    } else {
      const selectedGroups = learningReviewGroups
        .filter((item) => item.isSelected)
        .map((item) => makeGroupWithName(item.groupName));
      props.onSelectGroups(selectedGroups);
    }
    props.onClose();
  }

  function handleOnClickItem(item: LearningReviewGroup) {
    var newItems: LearningReviewGroup[] = [];
    item.isSelected = !item.isSelected;

    if (item.isAllGroup && item.isSelected) {
      newItems = learningReviewGroups.map((item) =>
        !item.isAllGroup ? { ...item, isSelected: true } : item
      );
    } else if (item.isAllGroup && !item.isSelected) {
      newItems = learningReviewGroups.map((item) =>
        !item.isAllGroup ? { ...item, isSelected: false } : item
      );
    } else if (!item.isAllGroup) {
      newItems = learningReviewGroups.map((item) =>
        item.isAllGroup ? { ...item, isSelected: false } : item
      );
    }

    setLearningReviewGroups(newItems);
  }

  function handleOnClickSortButton(e: React.MouseEvent<HTMLDivElement>) {
    setAnchorEl(e.currentTarget);
    setOpenSortStyleMenu(true);
  }

  if (!props.open) return null;

  return (
    <>
      <div className={styles.stack}>
        <div className={styles.container}>
          <div
            className={styles.groupSelectContainer}
            onClick={handleOnClickGroupSelectContainer}
          >
            <div className={styles.contentsContainer}>
              <ContainerNavigationView
                open={true}
                title={t("GroupSelectToLearn")}
                leftImageButtonImage={CloseIcon}
                onClickLeftImageButton={() => {
                  props.onClose();
                }}
                rightTextButtonText={t("Done")}
                rightTextButtonColor={
                  enabledDoneButton
                    ? "var(--text-accent-color)"
                    : "var(--text-disabled-color)"
                }
                onClickRightTextButton={() => {
                  if (!enabledDoneButton) return;
                  handleOnClickDoneButton();
                }}
              />

              <SpacingView height={8} />

              <div className={styles.topContainer}>
                <span className={styles.termsCountText}>{count}</span>
                <DropDownMenu
                  value={sortStyleTitle}
                  color="var(--text-primary-color)"
                  handleOnClick={handleOnClickSortButton}
                />
              </div>

              <div className={styles.listContainer}>
                {learningReviewGroups.length > 0 &&
                  learningReviewGroups.map((item, index) => (
                    <LearningReviewGroupCell
                      key={index}
                      item={item}
                      selectedAnswerTypes={props.selectedAnswerTypes}
                      onClickItem={handleOnClickItem}
                    />
                  ))}
              </div>

              {learningReviewGroups.length === 0 && (
                <div className={styles.emptyListContainer}>
                  <ListEmptyText text={t("LearningReviewNoGroupMessage")} />
                </div>
              )}
            </div>
          </div>
        </div>

        <BasicMenu
          isOpen={openSortStyleMenu}
          anchorEl={anchorEl}
          buttons={[
            {
              title: t("SortByNamed"),
              handleOnClose: () => {
                setOpenSortStyleMenu(false);
                setLearningReviewGroupSortStyleToLocalStorage(
                  LearningReviewSortStyle.named
                );
                setSortStyle(LearningReviewSortStyle.named);
                sortList(LearningReviewSortStyle.named);
              },
            },
            {
              title: t("SortByTermsCount"),
              handleOnClose: () => {
                setOpenSortStyleMenu(false);
                setLearningReviewGroupSortStyleToLocalStorage(
                  LearningReviewSortStyle.termsCount
                );
                setSortStyle(LearningReviewSortStyle.termsCount);
                sortList(LearningReviewSortStyle.termsCount);
              },
            },
            {
              title: t("SortByTermsCountReverse"),
              handleOnClose: () => {
                setOpenSortStyleMenu(false);
                setLearningReviewGroupSortStyleToLocalStorage(
                  LearningReviewSortStyle.termsCountReverse
                );
                setSortStyle(LearningReviewSortStyle.termsCountReverse);
                sortList(LearningReviewSortStyle.termsCountReverse);
              },
            },
            {
              title: t("SortByIncorrectTermsCount"),
              handleOnClose: () => {
                setOpenSortStyleMenu(false);
                setLearningReviewGroupSortStyleToLocalStorage(
                  LearningReviewSortStyle.incorrectTermsCount
                );
                setSortStyle(LearningReviewSortStyle.incorrectTermsCount);
                sortList(LearningReviewSortStyle.incorrectTermsCount);
              },
            },
          ]}
          handleOnClose={() => {
            setOpenSortStyleMenu(false);
          }}
        />
      </div>
    </>
  );
}
